import { useEffect, useState } from "react";
import { Download } from "lucide-react";
import {
	getOfflineVideos,
	removeOfflineVideo,
	getTotalStorageUsedMB,
	type OfflineVideoMetadata,
} from "@/utils/storage/offlineVideosStorage";
import { formatDuration } from "@/utils/dateAndTime";
// import { isNativePlatform } from "@/utils/storage/storage";
import { VideoPlayer } from "@/pages/Workshop/VideoPlayer";
// import { Link } from "@tanstack/react-router";
import { TrashIcon } from "@/components/icons/trash";
import { pauseDownload, cancelDownload } from "@/utils/videoDownloadService";
import { videoLog } from "@/components/VideoDownloadDebugger";
import { useOnlineStatusContext } from "@/context/OnlineStatusContext";
import { PlayIcon } from "@/components/icons/play";
import type { ProductDataMultimedia } from "./Workshop/MultimediaCard";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useSearch, useNavigate, useLocation } from "@tanstack/react-router";

const checkProductType = (
	productData: ProductDataMultimedia | undefined
): string | null => {
	if (!productData) return null;

	const typeMap: Record<string, string | null> = {
		webinar: "Webinar •",
		training: "Szkolenie •",
		course: "Kurs •",
		unknown: null,
	};

	return typeMap[productData.productType] ?? null;
};

const OfflineVideosPage = (): JSX.Element => {
	const [videos, setVideos] = useState<Array<OfflineVideoMetadata>>([]);
	const [totalStorageMB, setTotalStorageMB] = useState(0);
	const [loading, setLoading] = useState(true);
	const [selectedVideo, setSelectedVideo] =
		useState<OfflineVideoMetadata | null>(null);
	const { isOnline } = useOnlineStatusContext();

	// ⭐ Dodaj routing hooks
	const search = useSearch({ from: "/(app)/_app/offline-videos" });
	const navigate = useNavigate();
	const location = useLocation();

	// Pobierz productType z URL (default "training")
	const currentProductType = search.productType || "training";

	const loadVideos = async (): Promise<void> => {
		try {
			videoLog.info("OfflinePage", "Loading offline videos list", {});

			const videosData = await getOfflineVideos();
			const videosList = Object.values(videosData).sort((a, b) => {
				return (b.downloadCompletedAt || 0) - (a.downloadCompletedAt || 0);
			});
			setVideos(videosList);

			const total = await getTotalStorageUsedMB();
			setTotalStorageMB(total);

			videoLog.success(
				"OfflinePage",
				`Loaded ${videosList.length} videos, ${total.toFixed(2)} MB total`,
				{
					videoCount: videosList.length,
					totalStorageMB: total.toFixed(2),
				}
			);
		} catch (error) {
			console.error("Error loading offline videos:", error);
			videoLog.error("OfflinePage", "Error loading offline videos", {
				error: error instanceof Error ? error.message : String(error),
			});
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadVideos().catch(console.error);
	}, []);

	// Okresowe sprawdzanie postępu podczas pobierania
	useEffect(() => {
		const downloadingVideos = videos.filter((v) => v.status === "downloading");
		if (downloadingVideos.length === 0) return;

		const intervalId = setInterval(() => {
			void (async (): Promise<void> => {
				// Pobierz aktualne dane z storage
				const videosData = await getOfflineVideos();
				const updatedVideos = Object.values(videosData).sort((a, b) => {
					return (b.downloadCompletedAt || 0) - (a.downloadCompletedAt || 0);
				});

				// Sprawdź czy są zmiany w postępie
				const hasChanges = updatedVideos.some((video) => {
					const currentVideo = videos.find((v) => v.videoId === video.videoId);
					return (
						currentVideo &&
						(currentVideo.downloadProgress !== video.downloadProgress ||
							currentVideo.status !== video.status)
					);
				});

				if (hasChanges) {
					setVideos(updatedVideos);

					// Aktualizuj też całkowite użycie storage
					const total = await getTotalStorageUsedMB();
					setTotalStorageMB(total);
				}
			})();
		}, 1000);

		return (): void => {
			clearInterval(intervalId);
		};
	}, [videos]); // Zależność od videos, żeby interval się restartował gdy lista się zmieni

	const handleDelete = async (videoId: string): Promise<void> => {
		if (confirm("Czy na pewno chcesz usunąć to wideo?")) {
			videoLog.warning(
				"OfflinePage",
				"User confirmed delete video",
				{ videoId },
				videoId
			);

			try {
				await removeOfflineVideo(videoId);
				videoLog.success(
					"OfflinePage",
					"Video deleted successfully",
					{ videoId },
					videoId
				);
				await loadVideos();
			} catch (error) {
				console.error("Error deleting video:", error);
				videoLog.error(
					"OfflinePage",
					"Error deleting video",
					{
						error: error instanceof Error ? error.message : String(error),
						videoId,
					},
					videoId
				);
				alert("Błąd podczas usuwania wideo");
			}
		} else {
			videoLog.info(
				"OfflinePage",
				"User canceled delete",
				{ videoId },
				videoId
			);
		}
	};

	const handlePlay = (video: OfflineVideoMetadata): void => {
		videoLog.info(
			"OfflinePage",
			"User started playback",
			{
				videoName: video.videoName,
				videoSource: video.videoSource,
				vimeoId: video.vimeoId,
				originalVideoUrl: video.originalVideoUrl,
			},
			video.videoId
		);

		console.log("🎬 [OfflineVideosPage] Starting video playback:", {
			videoId: video.videoId,
			videoName: video.videoName,
			videoSource: video.videoSource,
			vimeoId: video.vimeoId,
			originalVideoUrl: video.originalVideoUrl,
			productData: video.productData,
			courseClassesId: video.courseClassesId,
		});

		setSelectedVideo(video);
	};

	const handlePauseDownload = async (
		video: OfflineVideoMetadata
	): Promise<void> => {
		videoLog.warning(
			"OfflinePage",
			"User paused download from list",
			{
				videoName: video.videoName,
				currentProgress: video.downloadProgress,
			},
			video.videoId
		);

		try {
			await pauseDownload(
				video.productData.productType,
				video.productData.productId,
				video.courseClassesId
			);
			videoLog.success(
				"OfflinePage",
				"Download paused successfully",
				{},
				video.videoId
			);
			await loadVideos();
		} catch (error) {
			console.error("Error pausing download:", error);
			videoLog.error(
				"OfflinePage",
				"Error pausing download",
				{
					error: error instanceof Error ? error.message : String(error),
				},
				video.videoId
			);
			alert("Błąd podczas wstrzymywania pobierania");
		}
	};

	// if (!isNativePlatform()) {
	// 	return (
	// 		<div className="p-4 text-center">
	// 			<p>Funkcja offline wideo jest dostępna tylko w aplikacji mobilnej.</p>
	// 		</div>
	// 	);
	// }

	if (loading) {
		return (
			<div className="p-4">
				<p>Ładowanie...</p>
			</div>
		);
	}

	const completedVideos = videos.filter((v) => v.status === "completed");
	// z completed video nalezy wyodrębnić szkolenia, webinary i kursy
	// przydzielaj według productData.productType
	const trainingVideos = completedVideos.filter(
		(v) => v.productData.productType === "training"
	);
	const webinarVideos = completedVideos.filter(
		(v) => v.productData.productType === "webinar"
	);
	const courseVideos = completedVideos.filter(
		(v) => v.productData.productType === "course"
	);
	console.log("completedVideos", completedVideos);

	const downloadingVideos = videos.filter(
		(v) => v.status === "downloading" || v.status === "paused"
	);
	// const pausedVideos = videos.filter((v) => v.status === "paused");

	const handleResume = (video: OfflineVideoMetadata): void => {
		// Redirect to workshop page to resume download
		alert(
			`Przejdź do kursu "${video.productData?.productName}" aby wznowić pobieranie`
		);
	};

	// ⭐ Wybierz odpowiednią listę na podstawie URL param
	const filteredVideos = currentProductType === "training"
		? trainingVideos
		: currentProductType === "webinar"
		? webinarVideos
		: courseVideos;

	return (
		<main className="py-3 px-safe-offset-3 min-h-[calc(100vh-176px)] overflow-x-hidden max-w-[890px] mx-auto">
			{/* breadcrumbs */}
			{/* <div className="flex text-fontDarkGray pb-4 line-clamp-1">
				<Link className="whitespace-nowrap flex-shrink-0" to="/user-profil">
					Profil
				</Link>
				<span className="px-4">&#8226;</span>
				<span className="truncate min-w-0">Pobrane materiały</span>
			</div> */}

			<div className="bg-blue-50 p-4 rounded-lg mb-6">
				<div className="flex items-center justify-between gap-4">
					<p className="text-sm text-gray-600 flex-1 min-w-0">
						Całkowite miejsce na dysku urządzenia zajmowane przez pobrane wideo
					</p>
					<p className="text-xl font-medium whitespace-nowrap flex-shrink-0">
						{totalStorageMB.toFixed(2)} MB
					</p>
				</div>
				<p className="text-xs text-gray-500 mt-4">
					Pobrane wideo: {completedVideos.length}
				</p>
			</div>

			{isOnline && downloadingVideos.length > 0 && (
				<div className="mb-6 space-y-3">
					{downloadingVideos.map((video) => (
						<div key={video.videoId} className="bg-white rounded-lg shadow-md">
							<h3 className="text-md px-5 pt-5 mb-3">{video.videoName}</h3>

							{/* Status i przyciski */}
							<div className="flex items-center justify-between px-5 pb-4">
								<p className="text-sm text-gray-400">
									{video.status === "paused"
										? "Pobieranie wstrzymane"
										: `Trwa pobieranie... ${Math.round(video.downloadProgress)}%`}
								</p>
								<div className="flex gap-2">
									{/* Wstrzymaj */}
									{video.status === "downloading" && (
										<button
											onClick={async () => {
												await handlePauseDownload(video);
											}}
											className="text-sm text-orange-600 underline hover:text-orange-800"
										>
											Wstrzymaj
										</button>
									)}

									{/* Wznów */}
									{video.status === "paused" && (
										<button
											onClick={() => {
												handleResume(video);
											}}
											className="text-sm text-blue-600 underline hover:text-blue-800"
										>
											Wznów
										</button>
									)}

									{/* ✅ NOWE: Anuluj */}
									<button
										onClick={async () => {
											if (
												confirm("Czy na pewno chcesz anulować to pobieranie?")
											) {
												try {
													await cancelDownload(
														video.productData.productType,
														video.productData.productId,
														video.courseClassesId
													);
													await loadVideos(); // Reload list
												} catch (error) {
													console.error("Cancel error:", error);
													alert("Błąd podczas anulowania");
												}
											}
										}}
										className="text-sm text-red-600 underline hover:text-red-800"
									>
										Anuluj
									</button>
								</div>
							</div>

							{/* Progress bar */}
							<div className="h-[5px] w-full bg-[#D5E5FF] rounded-b-xl overflow-hidden relative">
								{Math.round(video.downloadProgress) > 0 && (
									<div
										className="h-full bg-[#196DFE] transition-all duration-300 ease-out"
										style={{ width: `${Math.round(video.downloadProgress)}%` }}
									/>
								)}
							</div>
						</div>
					))}
				</div>
			)}

			{/* {pausedVideos.length > 0 && (
				<div className="mb-6">
					<h3 className="text-md font-semibold mb-2">Wstrzymane pobierania</h3>
					{pausedVideos.map((video) => (
						<div
							key={video.videoId}
							className="bg-gray-50 p-4 rounded-lg mb-2"
						>
							<p className="font-medium">{video.videoName}</p>
							<p className="text-xs text-gray-500 mt-1">{video.courseName}</p>
							<div className="flex gap-2 mt-2">
								<button
									onClick={() => {
										handleResume(video);
									}}
									className="text-sm text-blue-600 underline"
								>
									Wznów pobieranie
								</button>
								<button
									onClick={async () => {
										await handleDelete(video.videoId);
									}}
									className="text-sm text-red-600 underline"
								>
									Usuń
								</button>
							</div>
						</div>
					))}
				</div>
			)} */}

			{completedVideos.length === 0 ? (
				<div className="text-center py-12">
					<Download className="w-16 h-16 text-gray-300 mx-auto mb-4" />
					<p className="text-gray-500">Brak pobranych wideo</p>
					<p className="text-sm text-gray-400 mt-2">
						Pobierz wideo z lekcji, aby oglądać je offline
					</p>
				</div>
			) : (
				<Tabs className="w-full pb-4" value={currentProductType}>
					<TabsList className="w-full">
						<TabsTrigger
							className="w-1/3 p-2"
							value="training"
							onClick={async () => {
								await navigate({
									to: location.pathname,
									search: { productType: "training" },
									replace: true,
								});
							}}
						>
							Szkolenia
						</TabsTrigger>
						<TabsTrigger
							className="w-1/3 p-2"
							value="webinar"
							onClick={async () => {
								await navigate({
									to: location.pathname,
									search: { productType: "webinar" },
									replace: true,
								});
							}}
						>
							Webinary
						</TabsTrigger>
						<TabsTrigger
							className="w-1/3 p-2"
							value="course"
							onClick={async () => {
								await navigate({
									to: location.pathname,
									search: { productType: "course" },
									replace: true,
								});
							}}
						>
							Kursy
						</TabsTrigger>
					</TabsList>

					<TabsContent value="training" className="mt-4">
						<div className="space-y-3">
							{trainingVideos.map((video) => (
								<div
									key={video.videoId}
									className="bg-white p-5 pb-2 rounded-lg shadow-md"
								>
									<div className="flex justify-between items-start mb-2">
										<div className="flex-1">
											<h3 className="text-md">{video.videoName}</h3>
											{video.productData && (
												<>
													<p className="text-sm text-gray-500 mt-5">
														{checkProductType(video.productData)}{" "}
														{video.productData.productName}
													</p>
													<p className="text-sm text-gray-500 mt-1">
														{video.productData.authors.length > 0 &&
															video.productData.authors
																.map((author) => author.display_name)
																.join(", ")}
													</p>
												</>
											)}
										</div>
									</div>

									<div className="flex items-center justify-between text-xs text-gray-600 mt-6 mb-3">
										<span>
											{formatDuration(video.duration)} •{" "}
											{video.selectedResolution} • {video.fileSizeMB} MB
										</span>
									</div>

									<div className="flex justify-between">
										<button
											onClick={async () => {
												await handleDelete(video.videoId);
											}}
											className="p-4 pl-0 flex items-center gap-2"
										>
											<TrashIcon />{" "}
											<span className="text-sm underline">Usuń</span>
										</button>
										<div className="flex items-center justify-between md:justify-start">
											<p className="mr-2">Oglądaj offline</p>
											<button
												className="p-4 pr-0"
												onClick={() => {
													handlePlay(video);
												}}
											>
												<PlayIcon />
											</button>
										</div>
									</div>
								</div>
							))}
						</div>
					</TabsContent>

					<TabsContent value="webinar" className="mt-4">
						<div className="space-y-3">
							{webinarVideos.map((video) => (
								<div
									key={video.videoId}
									className="bg-white p-5 pb-2 rounded-lg shadow-md"
								>
									<div className="flex justify-between items-start mb-2">
										<div className="flex-1">
											<h3 className="text-md">{video.videoName}</h3>
											{video.productData && (
												<>
													<p className="text-sm text-gray-500 mt-5">
														{checkProductType(video.productData)}{" "}
														{video.productData.productName}
													</p>
													<p className="text-sm text-gray-500 mt-1">
														{video.productData.authors.length > 0 &&
															video.productData.authors
																.map((author) => author.display_name)
																.join(", ")}
													</p>
												</>
											)}
										</div>
									</div>

									<div className="flex items-center justify-between text-xs text-gray-600 mt-6 mb-3">
										<span>
											{formatDuration(video.duration)} •{" "}
											{video.selectedResolution} • {video.fileSizeMB} MB
										</span>
									</div>

									<div className="flex justify-between">
										<button
											onClick={async () => {
												await handleDelete(video.videoId);
											}}
											className="p-4 pl-0 flex items-center gap-2"
										>
											<TrashIcon />{" "}
											<span className="text-sm underline">Usuń</span>
										</button>
										<div className="flex items-center justify-between md:justify-start">
											<p className="mr-2">Oglądaj offline</p>
											<button
												className="p-4 pr-0"
												onClick={() => {
													handlePlay(video);
												}}
											>
												<PlayIcon />
											</button>
										</div>
									</div>
								</div>
							))}
						</div>
					</TabsContent>

					<TabsContent value="course" className="mt-4">
						<div className="space-y-3">
							{courseVideos.map((video) => (
								<div
									key={video.videoId}
									className="bg-white p-5 pb-2 rounded-lg shadow-md"
								>
									<div className="flex justify-between items-start mb-2">
										<div className="flex-1">
											<h3 className="text-md">{video.videoName}</h3>
											{video.productData && (
												<>
													<p className="text-sm text-gray-500 mt-5">
														{checkProductType(video.productData)}{" "}
														{video.productData.productName}
													</p>
													<p className="text-sm text-gray-500 mt-1">
														{video.productData.authors.length > 0 &&
															video.productData.authors
																.map((author) => author.display_name)
																.join(", ")}
													</p>
												</>
											)}
										</div>
									</div>

									<div className="flex items-center justify-between text-xs text-gray-600 mt-6 mb-3">
										<span>
											{formatDuration(video.duration)} •{" "}
											{video.selectedResolution} • {video.fileSizeMB} MB
										</span>
									</div>

									<div className="flex justify-between">
										<button
											onClick={async () => {
												await handleDelete(video.videoId);
											}}
											className="p-4 pl-0 flex items-center gap-2"
										>
											<TrashIcon />{" "}
											<span className="text-sm underline">Usuń</span>
										</button>
										<div className="flex items-center justify-between md:justify-start">
											<p className="mr-2">Oglądaj offline</p>
											<button
												className="p-4 pr-0"
												onClick={() => {
													handlePlay(video);
												}}
											>
												<PlayIcon />
											</button>
										</div>
									</div>
								</div>
							))}
						</div>
					</TabsContent>
				</Tabs>
			)}

			{/* Video Player Modal */}
			{selectedVideo && (
				<div className="fixed inset-0 z-50 bg-black">
					<VideoPlayer
						videoContent={{
							video_source: "local_memory",
						}}
						open="VIDEO"
						productData={selectedVideo.productData}
						courseClassesId={selectedVideo.courseClassesId}
						setOpen={(open) => {
							if (!open) {
								setSelectedVideo(null);
							}
						}}
					/>
				</div>
			)}
		</main>
	);
};

export default OfflineVideosPage;
